import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, FILES_BASE, formatBytes, formatDateDE, formatErrorDetail } from "@/lib/api";
import { StatusBadge, CategoryBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, FileText, Image as ImageIcon, Loader2, Factory, Share2 } from "lucide-react";
import { toast } from "sonner";

export default function UpdateDetail() {
  const { id } = useParams();
  const [update, setUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/updates/${id}`);
        setUpdate(data);
      } catch (e) {
        setError(formatErrorDetail(e.response?.data?.detail) || "Update nicht gefunden");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link in Zwischenablage kopiert");
    } catch {
      toast.error("Konnte Link nicht kopieren");
    }
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-24 text-slate-500" data-testid="detail-loading">
        <Loader2 className="h-6 w-6 mr-2 animate-spin" />
        Update wird geladen…
      </div>
    );

  if (error || !update)
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h2 className="font-heading text-2xl font-semibold text-slate-900">Update nicht verfügbar</h2>
        <p className="text-slate-600 mt-2">{error}</p>
        <Link to="/" className="inline-block mt-6">
          <Button variant="outline" className="border-slate-300" data-testid="back-to-feed">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zur Übersicht
          </Button>
        </Link>
      </div>
    );

  const attachments = update.attachments || [];
  const images = attachments.filter((a) => a.is_image);
  const files = attachments.filter((a) => !a.is_image);

  return (
    <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="update-detail">
      <Link to="/" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="back-link">
        <ArrowLeft className="h-4 w-4 mr-1" />
        Alle Updates
      </Link>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          {formatDateDE(update.date)}
        </span>
        <span className="text-slate-300">•</span>
        <StatusBadge status={update.status} />
        <CategoryBadge category={update.category} />
      </div>

      <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight text-balance leading-tight">
        {update.title}
      </h1>

      {update.anlagen && update.anlagen.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mt-5">
          <Factory className="h-4 w-4 text-slate-400" />
          {update.anlagen.map((a) => (
            <span
              key={a.id}
              className="text-sm px-3 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200"
            >
              {a.name}
            </span>
          ))}
        </div>
      )}

      <div className="my-8 flex items-center justify-between border-y border-slate-200 py-4">
        <div className="text-sm text-slate-500">
          Veröffentlicht am {formatDateDE(update.created_at)}
        </div>
        <Button variant="outline" size="sm" onClick={copyLink} className="border-slate-300" data-testid="share-link-button">
          <Share2 className="h-4 w-4 mr-2" />
          Link teilen
        </Button>
      </div>

      <div className="prose prose-slate max-w-none">
        <p className="text-lg text-slate-700 leading-relaxed whitespace-pre-wrap">
          {update.description}
        </p>
      </div>

      {images.length > 0 && (
        <section className="mt-10">
          <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">Bilder</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {images.map((img) => (
              <a
                key={img.id}
                href={`${FILES_BASE}/${img.id}`}
                target="_blank"
                rel="noreferrer"
                className="block group relative rounded-lg overflow-hidden bg-slate-100 border border-slate-200"
                data-testid={`detail-image-${img.id}`}
              >
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${img.url}`}
                  alt={img.original_name}
                  className="w-full h-64 object-cover group-hover:scale-[1.02] transition-transform duration-500"
                />
              </a>
            ))}
          </div>
        </section>
      )}

      {files.length > 0 && (
        <section className="mt-10">
          <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">Dateien zum Download</h2>
          <div className="space-y-2">
            {files.map((f) => (
              <a
                key={f.id}
                href={`${FILES_BASE}/${f.id}`}
                target="_blank"
                rel="noreferrer"
                download={f.original_name}
                className="flex items-center justify-between gap-3 p-4 rounded-lg border border-slate-200 hover:border-[#E2001A] hover:shadow-sm transition-all group"
                data-testid={`detail-download-${f.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="h-5 w-5 text-slate-500" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{f.original_name}</div>
                    <div className="text-xs text-slate-500">{formatBytes(f.size)} · {f.content_type}</div>
                  </div>
                </div>
                <div className="inline-flex items-center text-sm font-semibold text-[#E2001A] group-hover:text-[#C20015] flex-shrink-0">
                  <Download className="h-4 w-4 mr-1" />
                  Download
                </div>
              </a>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
